import {
  Col,
  Flex,
  Form,
  Input,
  JsonEditor,
  type JsonValue,
  MarkdownEditor,
  Paragraph,
  Row,
  Select,
  type FormInstance,
  type SelectOption,
} from "@ir/ui";
import ErrorBoundary from "@src/presentation/components/common/ErrorBoundary";
import { getInputValue } from "@src/presentation/pages/Organizations/utils/addEditFormUtils";
import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  createDeductionTypeKeyRules,
  generateDeductionTypeKeyFromName,
} from "./deductionTypeKeyValidation";

export interface DeductionTypeAddEditFormValues {
  name: string;
  deductionTypeKey: string;
  deductionTypeCategoryId: number | null;
  helpText: string;
  metadata: string;
}

export interface DeductionCategorySelectOption {
  categoryId: number;
  categoryName: string;
}

interface MasterDataDeductionTypeAddEditFormProps {
  form: FormInstance<DeductionTypeAddEditFormValues>;
  mode: "create" | "edit";
  initialValues: DeductionTypeAddEditFormValues;
  categoryOptions: DeductionCategorySelectOption[];
  categoriesLoading?: boolean;
  showHelpText?: boolean;
  onSubmit: (values: DeductionTypeAddEditFormValues) => Promise<void> | void;
  onFormValidityChange?: (isSubmittable: boolean) => void;
}

function parseIdFromSelect(value: unknown): number | null {
  if (value == null) return null;
  if (typeof value === "object" && "value" in (value as object)) {
    const raw = (value as SelectOption).value;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function parseStoredJson(value: string | undefined): JsonValue {
  const trimmed = (value ?? "").trim();
  if (!trimmed) {
    return {};
  }
  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (parsed != null && typeof parsed === "object") {
      return parsed as JsonValue;
    }
    return {};
  } catch {
    return {};
  }
}

interface JsonEditorFieldProps {
  value?: string;
  onChange?: (next: string) => void;
}

const JsonEditorField = ({ value, onChange }: JsonEditorFieldProps) => {
  return (
    <JsonEditor
      value={parseStoredJson(value)}
      onChange={(next) => onChange?.(JSON.stringify(next, null, 2))}
      rootName="metadata"
      collapse={2}
    />
  );
};

interface MarkdownEditorFieldProps {
  value?: string;
  onChange?: (next: string) => void;
}

const MarkdownEditorField = ({
  value,
  onChange,
}: MarkdownEditorFieldProps) => {
  return (
    <MarkdownEditor
      value={value ?? ""}
      onChange={onChange}
      placeholder="Optional help text"
      height="240px"
      showToolbar
      enablePreview
    />
  );
};

interface CategorySelectFieldProps {
  value?: number | null;
  onChange?: (next: number | null) => void;
  options: SelectOption[];
  isLoading?: boolean;
  selectKey: string;
}

const CategorySelectField = ({
  value,
  onChange,
  options,
  isLoading = false,
  selectKey,
}: CategorySelectFieldProps) => {
  const selected = options.find((option) => option.value === String(value));
  return (
    <Select
      key={`${selectKey}-${value ?? "none"}-${options.length}`}
      size="medium"
      isSearchable
      isClearable
      isLoading={isLoading}
      placeholder="Select category"
      options={options}
      defaultValue={selected}
      onChange={(next) => onChange?.(parseIdFromSelect(next))}
      aria-label="Deduction category"
    />
  );
};

export const MasterDataDeductionTypeAddEditForm = ({
  form,
  mode,
  initialValues,
  categoryOptions,
  categoriesLoading = false,
  showHelpText = false,
  onSubmit,
  onFormValidityChange,
}: MasterDataDeductionTypeAddEditFormProps) => {
  const isKeyEditedManuallyRef = useRef(false);
  const typeKeyDisabled = useMemo(() => mode === "edit", [mode]);
  const selectOptions = useMemo<SelectOption[]>(
    () =>
      categoryOptions.map((option) => ({
        label: option.categoryName,
        value: String(option.categoryId),
      })),
    [categoryOptions],
  );

  const updateFormValidity = useCallback(() => {
    const hasErrors = form
      .getFieldsError()
      .some((field) => (field.errors?.length ?? 0) > 0);

    const values = form.getFieldsValue();
    const name = values.name?.trim() ?? "";
    const deductionTypeKey = values.deductionTypeKey?.trim() ?? "";
    const helpText = values.helpText?.trim() ?? "";
    const metadata = values.metadata?.trim() ?? "";
    const categoryId = values.deductionTypeCategoryId ?? null;

    const hasRequiredValues =
      name !== "" && (mode === "edit" || deductionTypeKey !== "");

    const isDirty =
      mode === "create"
        ? true
        : name !== (initialValues.name?.trim() ?? "") ||
          (showHelpText &&
            helpText !== (initialValues.helpText?.trim() ?? "")) ||
          metadata !== (initialValues.metadata?.trim() ?? "") ||
          categoryId !== (initialValues.deductionTypeCategoryId ?? null);

    onFormValidityChange?.(hasRequiredValues && !hasErrors && isDirty);
  }, [form, initialValues, mode, onFormValidityChange, showHelpText]);

  useEffect(() => {
    isKeyEditedManuallyRef.current = mode === "edit";
    form.setFieldsValue(initialValues);
    updateFormValidity();
  }, [form, initialValues, mode, updateFormValidity]);

  const nameRules = useMemo(
    () => [
      { required: true, message: "Name is required" },
      {
        max: 255,
        message: "Name must be less than or equal to 255 characters.",
      },
    ],
    [],
  );

  const typeKeyRules = useMemo(() => createDeductionTypeKeyRules(), []);

  const syncTypeKeyFromName = (valueOrEvent: unknown) => {
    if (typeKeyDisabled || isKeyEditedManuallyRef.current) return;
    form.setFieldsValue({
      deductionTypeKey: generateDeductionTypeKeyFromName(
        getInputValue(valueOrEvent),
      ),
    });
    form.validateFields(["deductionTypeKey"]).catch(() => {});
  };

  const handleNameChange = (valueOrEvent: unknown) => {
    form.setFieldsValue({
      name: getInputValue(valueOrEvent),
    });
    syncTypeKeyFromName(valueOrEvent);
    form.validateFields(["name"]).catch(() => {});
    updateFormValidity();
  };

  const handleTypeKeyChange = () => {
    if (typeKeyDisabled) return;
    isKeyEditedManuallyRef.current = true;
    form.validateFields(["deductionTypeKey"]).catch(() => {});
    updateFormValidity();
  };

  return (
    <ErrorBoundary>
      <Form
        form={form}
        onFinish={onSubmit}
        onFieldsChange={updateFormValidity}
        className="master-data-deduction-type-add-edit"
      >
        <Row>
          <Col span={6}>
            <Form.Item
              name="name"
              label="Name"
              validateTrigger={["onChange", "onBlur"]}
              rules={nameRules}
            >
              <Input
                placeholder="Deduction type name"
                onChange={handleNameChange}
                onInput={handleNameChange}
                onKeyUp={handleNameChange}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="deductionTypeKey"
              label="Deduction Type Key"
              validateTrigger={["onChange", "onBlur"]}
              rules={typeKeyDisabled ? undefined : typeKeyRules}
              helpText={
                typeKeyDisabled
                  ? "Once key is generated, it cannot be changed."
                  : undefined
              }
            >
              <Input
                placeholder="DEDUCTION-TYPE-KEY"
                isDisabled={typeKeyDisabled}
                onChange={handleTypeKeyChange}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={6}>
            <Form.Item name="deductionTypeCategoryId" label="Category">
              <CategorySelectField
                options={selectOptions}
                isLoading={categoriesLoading}
                selectKey={`${mode}-${initialValues.deductionTypeCategoryId ?? "none"}`}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Flex direction="column" gap={0.5}>
              <Paragraph className="m-0 custom-label">Metadata</Paragraph>
              <Form.Item name="metadata" noStyle>
                <JsonEditorField />
              </Form.Item>
            </Flex>
          </Col>
        </Row>

        {showHelpText ? (
          <Row>
            <Col span={12}>
              <Form.Item name="helpText" label="Help Text">
                <MarkdownEditorField />
              </Form.Item>
            </Col>
          </Row>
        ) : null}
      </Form>
    </ErrorBoundary>
  );
};
